import { z } from "zod";
import { createEndpoint, createEndpointWithNumberedPagination, createEndpointWithPagination, StandardSuccessResponseSchema } from "../createEndpoint";
import { HttpClient, QueryParams } from "../httpClient";
import {
    PageSchemaSchema,
    CreatePageSchemaRequestSchema,
    UpdatePageSchemaRequestSchema,
    AdminPageSchema,
    CreatePageRequestSchema,
    UpdatePageRequestSchema,
    PageVersionSchema,
    RestoreVersionRequestSchema,
    PageCollectionSchema,
    CreatePageCollectionRequestSchema,
    UpdatePageCollectionRequestSchema,
} from "../schemas/pages";

const PageListMetaSchema = z.object({
    total: z.number().optional(),
    count: z.number().optional(),
});

// Query params for listing page schemas
export interface AdminPageSchemaListQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
}

// Query params for listing pages
export interface AdminPageListQueryParams extends QueryParams {
    schema_id?: number;
    no_schema?: boolean;
    tags?: string[];
    status?: 'draft' | 'published';
    search?: string;
    sort?: 'updated_at' | 'created_at' | 'path' | 'title' | 'status';
    direction?: 'asc' | 'desc';
    limit?: number;
    cursor?: string;
}

// Query params for listing page versions
export interface AdminPageVersionListQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
}

export interface AdminPageCollectionListQueryParams extends QueryParams {
    search?: string;
    page?: number;
    per_page?: number;
}

export function createAdminPagesApi(http: HttpClient, baseUrl: string) {
    const adminSchemasBaseUrl = `${baseUrl}/admin/page-schemas`;
    const adminPagesBaseUrl = `${baseUrl}/admin/pages`;
    const adminPageCollectionsBaseUrl = `${baseUrl}/admin/page-collections`;

    return {
        // ==================
        // Page Schemas CRUD
        // ==================

        /**
         * List all page schemas with pagination
         * GET /v2/admin/page-schemas
         */
        listSchemas: createEndpointWithPagination<
            z.infer<typeof PageSchemaSchema>,
            unknown,
            unknown,
            AdminPageSchemaListQueryParams
        >(http, "GET", "", {
            base: adminSchemasBaseUrl,
            itemSchema: PageSchemaSchema,
        }),

        /**
         * Get a single page schema by ID
         * GET /v2/admin/page-schemas/:id
         */
        getSchema: createEndpoint<
            z.infer<typeof PageSchemaSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "GET", "/:id", {
            base: adminSchemasBaseUrl,
            responseSchema: PageSchemaSchema,
            params: ["id"],
        }),

        /**
         * Create a new page schema
         * POST /v2/admin/page-schemas
         */
        createSchema: createEndpoint<
            z.infer<typeof PageSchemaSchema>,
            z.infer<typeof CreatePageSchemaRequestSchema>
        >(http, "POST", "", {
            base: adminSchemasBaseUrl,
            responseSchema: PageSchemaSchema,
        }),

        /**
         * Update an existing page schema
         * PUT /v2/admin/page-schemas/:id
         */
        updateSchema: createEndpoint<
            z.infer<typeof PageSchemaSchema>,
            z.infer<typeof UpdatePageSchemaRequestSchema>,
            QueryParams,
            { id: number }
        >(http, "PUT", "/:id", {
            base: adminSchemasBaseUrl,
            responseSchema: PageSchemaSchema,
            params: ["id"],
        }),

        /**
         * Delete a page schema (soft delete)
         * DELETE /v2/admin/page-schemas/:id
         */
        deleteSchema: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "DELETE", "/:id", {
            base: adminSchemasBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["id"],
        }),

        // ==================
        // Pages CRUD
        // ==================

        /**
         * List all pages with optional filtering
         * GET /v2/admin/pages
         */
        listPages: createEndpointWithPagination<
            z.infer<typeof AdminPageSchema>,
            z.infer<typeof PageListMetaSchema>,
            unknown,
            AdminPageListQueryParams
        >(http, "GET", "", {
            base: adminPagesBaseUrl,
            itemSchema: AdminPageSchema,
            metaSchema: PageListMetaSchema,
        }),

        /**
         * List distinct page tags
         * GET /v2/admin/pages/tags
         */
        listTags: createEndpoint<
            string[]
        >(http, "GET", "/tags", {
            base: adminPagesBaseUrl,
            responseSchema: z.array(z.string()),
        }),

        /**
         * Get a single page by ID
         * GET /v2/admin/pages/:id
         */
        getPage: createEndpoint<
            z.infer<typeof AdminPageSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "GET", "/:id", {
            base: adminPagesBaseUrl,
            responseSchema: AdminPageSchema,
            params: ["id"],
        }),

        /**
         * Create a new page
         * POST /v2/admin/pages
         */
        createPage: createEndpoint<
            z.infer<typeof AdminPageSchema>,
            z.infer<typeof CreatePageRequestSchema>
        >(http, "POST", "", {
            base: adminPagesBaseUrl,
            responseSchema: AdminPageSchema,
        }),

        /**
         * Update an existing page (creates version history entry)
         * PUT /v2/admin/pages/:id
         */
        updatePage: createEndpoint<
            z.infer<typeof AdminPageSchema>,
            z.infer<typeof UpdatePageRequestSchema>,
            QueryParams,
            { id: number }
        >(http, "PUT", "/:id", {
            base: adminPagesBaseUrl,
            responseSchema: AdminPageSchema,
            params: ["id"],
        }),

        /**
         * Delete a page (soft delete)
         * DELETE /v2/admin/pages/:id
         */
        deletePage: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "DELETE", "/:id", {
            base: adminPagesBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["id"],
        }),

        // ==================
        // Version Management
        // ==================

        /**
         * List page version history
         * GET /v2/admin/pages/:id/versions
         */
        listVersions: createEndpointWithPagination<
            z.infer<typeof PageVersionSchema>,
            unknown,
            unknown,
            AdminPageVersionListQueryParams,
            { id: number }
        >(http, "GET", "/:id/versions", {
            base: adminPagesBaseUrl,
            itemSchema: PageVersionSchema,
            params: ["id"],
        }),

        /**
         * Restore a page to a specific version
         * POST /v2/admin/pages/:id/restore-version
         */
        restoreVersion: createEndpoint<
            z.infer<typeof AdminPageSchema>,
            z.infer<typeof RestoreVersionRequestSchema>,
            QueryParams,
            { id: number }
        >(http, "POST", "/:id/restore-version", {
            base: adminPagesBaseUrl,
            responseSchema: AdminPageSchema,
            params: ["id"],
        }),

        // ==================
        // Page Collections CRUD
        // ==================

        /**
         * List page collections with numbered pagination
         * GET /v2/admin/page-collections
         */
        listCollections: createEndpointWithNumberedPagination<
            z.infer<typeof PageCollectionSchema>,
            unknown,
            unknown,
            AdminPageCollectionListQueryParams
        >(http, "GET", "", {
            base: adminPageCollectionsBaseUrl,
            itemSchema: PageCollectionSchema,
        }),

        /**
         * Get a single page collection by ID
         * GET /v2/admin/page-collections/:id
         */
        getCollection: createEndpoint<
            z.infer<typeof PageCollectionSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "GET", "/:id", {
            base: adminPageCollectionsBaseUrl,
            responseSchema: PageCollectionSchema,
            params: ["id"],
        }),

        /**
         * Create a page collection
         * POST /v2/admin/page-collections
         */
        createCollection: createEndpoint<
            z.infer<typeof PageCollectionSchema>,
            z.infer<typeof CreatePageCollectionRequestSchema>
        >(http, "POST", "", {
            base: adminPageCollectionsBaseUrl,
            responseSchema: PageCollectionSchema,
        }),

        /**
         * Update a page collection
         * PUT /v2/admin/page-collections/:id
         */
        updateCollection: createEndpoint<
            z.infer<typeof PageCollectionSchema>,
            z.infer<typeof UpdatePageCollectionRequestSchema>,
            QueryParams,
            { id: number }
        >(http, "PUT", "/:id", {
            base: adminPageCollectionsBaseUrl,
            responseSchema: PageCollectionSchema,
            params: ["id"],
        }),

        /**
         * Delete a page collection
         * DELETE /v2/admin/page-collections/:id
         */
        deleteCollection: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "DELETE", "/:id", {
            base: adminPageCollectionsBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["id"],
        }),
    };
}
